import { createContext, useState, useEffect, type ReactNode } from 'react';
import { useMarkets as useApiMarkets } from '@/backend/hooks/use-markets';
import { MARKETS as HARDCODED_MARKETS } from '@/solana/constants';
import type { Market, Product } from './types';
import { debug } from '@/lib/debug';

type MarketsContextType = {
  markets: Market[];
  products: Product[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
};

// Remove duplicate type definitions since they're imported from types.ts
/*
export type Market = {
  symbol: string;
  baseSymbol: string;
  quoteSymbol: string;
  binanceSymbol: string;
  basePrecision: number;
  quotePrecision: number;
  minOrderSize: number;
  tickSize: number;
  isActive: boolean;
  isFeatured: boolean;
  healthStatus: string;
  oracleSource?: string;
};
*/

export const MarketsContext = createContext<MarketsContextType | null>(null);

const CACHE_KEY = 'nordex_markets_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Validate a market has required fields to prevent crashes
const isValidMarket = (m: unknown): m is Market => {
  if (!m || typeof m !== 'object') return false;
  const market = m as Record<string, unknown>;
  return (
    typeof market.symbol === 'string' &&
    typeof market.baseSymbol === 'string' &&
    typeof market.quoteSymbol === 'string' &&
    typeof market.providerSymbol === 'string' &&
    market.symbol.length > 0 &&
    market.providerSymbol.length > 0
  );
};

export function MarketsProvider({ children }: { children: ReactNode }) {
  const { data: apiMarkets, isLoading, error, refetch } = useApiMarkets();
  const [markets, setMarkets] = useState<Market[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  debug.log('MarketsProvider', 'apiMarkets:', apiMarkets?.length, 'isLoading:', isLoading, 'error:', error);

  useEffect(() => {
    // 1. Try to load from localStorage cache first (instant)
    if (!isInitialized) {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          
          if (age < CACHE_TTL) {
            // Filter out any invalid markets from cache
            const validMarkets = (data as unknown[]).filter(isValidMarket);
            if (validMarkets.length > 0) {
              debug.log('Markets', `Loaded ${validMarkets.length} markets from cache (age: ${Math.round(age/1000)}s)`);
              setMarkets(validMarkets);
              setIsInitialized(true);
              return; // Use cached data
            } else {
              debug.warn('Markets', 'Cache contained no valid markets, will fetch fresh data');
            }
          } else {
            debug.log('Markets', 'Cache expired, fetching fresh data');
          }
        } catch (e) {
          debug.warn('Markets', 'Failed to parse cached markets:', e);
        }
      }
    }

    // 2. Load from API if cache miss/stale
    if (apiMarkets && apiMarkets.length > 0) {
      // Filter out any invalid markets from API response
      const validMarkets = apiMarkets.filter(isValidMarket);
      debug.log('Markets', `Loaded ${validMarkets.length} valid markets from API (${apiMarkets.length} total)`);
      setMarkets(validMarkets);
      setIsInitialized(true);
      
      // Cache valid markets for next time
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ data: validMarkets, timestamp: Date.now() })
        );
      } catch (e) {
        debug.warn('Markets', 'Failed to cache markets:', e);
      }
    }
    // 3. Fallback to hardcoded markets on error
    else if (error && markets.length === 0) {
      debug.warn('Markets', 'API failed, using hardcoded markets fallback (10 markets)');
      const fallbackMarkets = Object.entries(HARDCODED_MARKETS).map(([symbol, config]) => ({
        symbol,
        baseSymbol: config.baseSymbol,
        quoteSymbol: config.quoteSymbol,
        providerSymbol: config.binanceSymbol, // Use binanceSymbol as providerSymbol
        binanceSymbol: config.binanceSymbol, // Keep for backward compatibility
        basePrecision: config.basePrecision,
        quotePrecision: config.quotePrecision,
        minOrderSize: config.minOrderSize,
        minOrderValueUsd: 10, // Default $10 minimum for hardcoded markets
        tickSize: config.tickSize,
        isActive: true,
        isFeatured: true,
        healthStatus: 'unknown',
        oracleSource: 'auto',
      }));
      setMarkets(fallbackMarkets);
      setIsInitialized(true);
    }
  }, [apiMarkets, error, isInitialized, markets.length]);

  // Convert markets to products
  const products: Product[] = markets.map(m => ({
    id: m.symbol,
    symbol: m.symbol,
    base: {
      code: m.baseSymbol,
      precision: m.basePrecision,
      stepSize: String(m.minOrderSize),
    },
    quote: {
      code: m.quoteSymbol,
      precision: m.quotePrecision,
      tickSize: String(m.tickSize),
    },
    minOrderValueUsd: m.minOrderValueUsd || 10, // Default $10 if not set
  }));

  return (
    <MarketsContext.Provider
      value={{
        markets,
        products,
        isLoading: isLoading && markets.length === 0,
        error: error as Error | null,
        refresh: refetch,
      }}
    >
      {children}
    </MarketsContext.Provider>
  );
}

